import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Check, ArrowRight, AlertCircle } from 'lucide-react';

const SubscriptionSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { api } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    const check = async () => {
      try {
        const res = await api.get(`/stripe/checkout-status/${sessionId}`);
        if (res.data.payment_status === 'paid') setStatus('success');
        else setStatus('pending');
      } catch { setStatus('error'); }
    };
    check();
    const interval = setInterval(check, 3000);
    return () => clearInterval(interval);
  }, [sessionId, api]);

  return (
    <div className="min-h-screen bg-[#050a14] flex items-center justify-center p-4" data-testid="subscription-success-page">
      <div className="max-w-md w-full bg-[#111827] border border-white/10 rounded-2xl p-8 text-center">
        {status === 'loading' && (
          <>
            <div className="w-12 h-12 border-2 border-[#009b3a] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm text-gray-400">{t('subscriptionPages.verifyingPayment')}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#009b3a]/20 flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-[#009b3a]" />
            </div>
            <h1 className="text-xl font-bold text-white mb-2">{t('subscriptionPages.successTitle')}</h1>
            <p className="text-sm text-gray-400 mb-6">{t('subscriptionPages.successBody')}</p>
            <button onClick={() => navigate('/dashboard')} data-testid="go-dashboard" className="flex items-center gap-2 mx-auto px-6 py-3 rounded-xl font-semibold text-sm text-[#050a14] bg-[#009b3a] hover:bg-[#00b344] transition-all">
              {t('subscriptionPages.goToDashboard')} <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}
        {status === 'pending' && (
          <>
            <div className="w-12 h-12 border-2 border-[#fedd00] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm text-gray-400">{t('subscriptionPages.processingPayment')}</p>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-xl font-bold text-white mb-2">{t('subscriptionPages.errorTitle')}</h1>
            <p className="text-sm text-gray-400 mb-6">{t('subscriptionPages.errorBody')}</p>
            <button onClick={() => navigate('/dashboard')} className="text-sm text-[#009b3a] hover:underline">{t('subscriptionPages.backToDashboard')}</button>
          </>
        )}
      </div>
    </div>
  );
};

const SubscriptionCancelPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  return (
    <div className="min-h-screen bg-[#050a14] flex items-center justify-center p-4" data-testid="subscription-cancel-page">
      <div className="max-w-md w-full bg-[#111827] border border-white/10 rounded-2xl p-8 text-center">
        <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-white mb-2">{t('subscriptionPages.cancelTitle')}</h1>
        <p className="text-sm text-gray-400 mb-6">{t('subscriptionPages.cancelBody')}</p>
        <button onClick={() => navigate('/dashboard')} className="px-6 py-3 rounded-xl font-semibold text-sm text-[#050a14] bg-[#009b3a] hover:bg-[#00b344] transition-all">
          {t('subscriptionPages.backToDashboard')}
        </button>
      </div>
    </div>
  );
};

export { SubscriptionSuccessPage, SubscriptionCancelPage };
